import { useNavigate } from '@tanstack/react-router'
import { useEffect } from 'react'

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  SelectField,
  TextField,
} from '@/components/ui'
import { useToast } from '@/hooks'
import { useIsTrialAccount } from '@/modules/account'
import {
  useFolders,
  useFoldersLoaders,
  useLoadingFolders,
} from '@/modules/folders'
import {
  useProject,
  useProjectActions,
  useProjectMutating,
  useProjectMutations,
} from '@/modules/projects'
import type { Project } from '@/modules/projects/stores'
import {
  useDialogActions,
  useDialogData,
  useDialogIsOpen,
  useForm,
} from '@/stores'
import { getProjectPath } from '@/utils'

const dialogId = 'edit-project'

export const EditProjectDialog = () => {
  const { successToast, errorToast } = useToast()
  const navigate = useNavigate()
  const open = useDialogIsOpen(dialogId)
  const dialogData = useDialogData() as Project
  const { closeDialog } = useDialogActions()
  const { data, errors, setFieldError, setFieldValue } = useForm()

  const project = useProject()
  const { setProject } = useProjectActions()
  const { updateProject } = useProjectMutations()
  const { fetchFolders } = useFoldersLoaders()
  const folders = useFolders()
  const isLoadingFolders = useLoadingFolders()
  const isTrial = useIsTrialAccount()
  const isUpdating = useProjectMutating()

  useEffect(() => {
    fetchFolders('project')
  }, [])

  useEffect(() => {
    if (!dialogData || !open) return
    Object.keys(dialogData).forEach((field) => {
      setFieldValue(field, dialogData[field as keyof Project] as string)
    })
  }, [open, dialogData])

  useEffect(() => {
    if (!open || project || !dialogData?.slug) {
      return
    }
    setProject(dialogData)
  }, [open, project, dialogData])

  // A folder whose projects are all archived is left out of the list.
  const ownFolder = dialogData?.folder
  const isOwnFolderListed = folders.some(
    (folder) => folder.id === ownFolder?.id,
  )
  const folderOptions =
    ownFolder && !isOwnFolderListed ? [...folders, ownFolder] : folders

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const { name, folderId } = data as Partial<Project>

    if (!name || name.trim().length === 0) {
      setFieldError('name', 'Name is required')
      return
    }

    updateProject({ name, folder_id: folderId })
      .then((project) => {
        closeDialog(true)
        successToast('Updated project successfully')
        navigate({ to: getProjectPath(project) })
      })
      .catch((error) => errorToast('Failed to update project', error))
  }

  return (
    <Dialog open={open} onOpenChange={(open) => !open && closeDialog()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Project</DialogTitle>
          <DialogDescription>
            Enter a changes for the project "{project?.name}".
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit}>
          <div className="py-4 space-y-4">
            <div className="space-y-6">
              <TextField
                label="Project Name"
                id="project-name"
                placeholder="Enter project name"
                value={data?.name as string}
                error={errors?.name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setFieldValue('name', e.target.value)
                }
                required
                autoFocus
              />
              {!isTrial && (
                <SelectField
                  label="Folder"
                  id="folder-name"
                  placeholder="Select a folder"
                  value={`${data?.folderId}`}
                  onValueChange={(value: string) => {
                    setFieldValue('folderId', value)
                  }}
                  isLoading={isLoadingFolders}
                  options={folderOptions.map((folder) => ({
                    label: folder.name,
                    value: `${folder.id}`,
                  }))}
                />
              )}
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button theme="outline" onClick={() => closeDialog(false)}>
              Cancel
            </Button>
            <Button type="submit" isLoading={isUpdating}>
              Edit Project
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

EditProjectDialog.id = dialogId
